
import { defineComponent, ref } from "vue";
import FormInput from "@/components/form/inputs/FormInput.vue";
import Modal from "@/components/ui/Modal.vue";
import Spinner from "@/components/ui/Spinner.vue";
import { FormElementData } from "@/models/FormElementData";
import { FormElementState } from "@/models/FormElementState";
import { PossibleValues, SelectOption } from "@/common/FormTools";
import trackingMappingsService from "@/services/tracking-mappings.service";
import {
  TrackingMappingCarrier,
  TrackingMappingTable,
} from "@/models/TrackingMapping";

export type TrackingStatusesListMethods = {
  loadCarriers: () => Promise<void>;
  carrierChange: (fieldId: string, newValue: PossibleValues) => void;
  loadTable: (supplierId: number) => Promise<void>;
  closeModal: () => void;
};

export type TrackingStatusesListState = {
  // The endpoint refuses anyone who is not internal staff. The nav link is
  // already hidden for them; this covers someone typing the URL in.
  isRefused: boolean;
  isLoading: boolean;
  showModal: boolean;
  carriers: TrackingMappingCarrier[];
  carrierElementData: FormElementData;
  carrierInitialState: FormElementState;
  table: TrackingMappingTable | null;
};

// Full class strings, so Tailwind's purge keeps them.
const toneClasses: Record<string, string> = {
  accepted: "bg-yellow-50 border-yellow-200",
  transit: "bg-blue-50 border-blue-200",
  returned: "bg-red-50 border-red-200",
  delivered: "bg-green-50 border-green-200",
  none: "bg-gray-100 border-gray-200",
};

export default defineComponent({
  name: "TrackingStatusesList",
  components: {
    FormInput,
    Modal,
    Spinner,
  },
  setup() {
    const methods = {} as TrackingStatusesListMethods;

    const state = ref<TrackingStatusesListState>({
      isRefused: false,
      isLoading: false,
      showModal: false,
      carriers: [],
      carrierElementData: {
        id: "carrier",
        type: "select",
        label: "tracking_statuses.carrier",
        options: (): SelectOption[] =>
          state.value.carriers.map((carrier) => ({
            value: String(carrier.supplier_id),
            label: `${carrier.name} (${carrier.status_count})`,
          })),
        disabledEmptyOption: true,
        class: "flex flex-col",
      },
      carrierInitialState: { value: "" },
      table: null,
    });

    methods.loadCarriers = async (): Promise<void> => {
      try {
        const response = await trackingMappingsService.carriers();
        state.value.carriers = response.data.carriers ?? [];

        const first = state.value.carriers[0];
        if (!first) return;

        state.value.carrierInitialState = { value: String(first.supplier_id) };
        await methods.loadTable(first.supplier_id);
      } catch {
        state.value.isRefused = true;
      }
    };

    methods.carrierChange = (fieldId: string, newValue: PossibleValues) => {
      if (!newValue) return;

      methods.loadTable(Number(newValue));
    };

    methods.loadTable = async (supplierId: number): Promise<void> => {
      state.value.isLoading = true;

      try {
        const response = await trackingMappingsService.table(supplierId);
        state.value.table = response.data;
      } catch {
        state.value.table = null;
        state.value.showModal = true;
      } finally {
        state.value.isLoading = false;
      }
    };

    methods.closeModal = () => {
      state.value.showModal = false;
    };

    methods.loadCarriers();

    return {
      state,
      methods,
      toneClasses,
    };
  },
});
